import { JSX, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

type CompanyCardProps = {
    title: string,
    /** How many rows the list inside holds, when that is knowable from the record. */
    count?: number,
    /** One line under the title, for what the list is for rather than what it contains. */
    note?: string,
    children: ReactNode,
};

/**
 * One titled card on the company screen.
 *
 * The screen it replaced built each of these out of a nested `<Show>` with `actions={ false }`
 * and a `title=" "` to suppress the chrome, eight times over: eight record contexts, eight
 * fetches of the same company, and a heading that was a 24px `<span>` in a `<Box>`. A card is a
 * card, so it is one component now, and the count in the corner is the thing the old headings
 * could not tell you without opening them.
 */
export const CompanyCard: (props: CompanyCardProps) => JSX.Element =
    (props: CompanyCardProps): JSX.Element => {
        return (
            <Box sx={ {
                width: '100%',
                border: '1px solid',
                borderColor: 'var(--wf-line, rgba(1, 48, 66, 0.13))',
                borderRadius: 1,
                background: 'var(--wf-panel-2, #ffffff)',
                padding: '12px 14px 14px 14px',
            } }>
                <Box sx={ {
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: '8px',
                    marginBottom: props.note === undefined ? '8px' : '2px',
                } }>
                    <Typography sx={ {
                        fontSize: 'var(--wf-eyebrow, 9.5px)',
                        letterSpacing: 'var(--wf-track, 0.16em)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        color: 'text.secondary',
                    } }>
                        { props.title }
                    </Typography>
                    { (props.count !== undefined) &&
                        <Typography sx={ {
                            fontFamily: 'var(--wf-font-mono, monospace)',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: props.count === 0 ? 'text.secondary' : 'text.primary',
                        } }>
                            { props.count }
                        </Typography>
                    }
                </Box>
                { (props.note !== undefined) &&
                    <Typography sx={ { fontSize: '12px', color: 'text.secondary', marginBottom: '8px' } }>
                        { props.note }
                    </Typography>
                }
                { props.children }
            </Box>
        );
    }
